<script setup>
import { InitViewIDs } from '@src/config';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';

import { ViewportToolMode } from '@/src/store/contours/types/tool-modes-type';

import { useAlert } from '../composables/useAlert';
import { useFlowCalculatorStore } from '../store/calculators/FlowCalculatorStore';
import { FlowPhaseOffsetCorrection } from '../store/calculators/FlowPhaseOffsetCorrection';
import {
  FlowVesselTracker,
  segmentVesselFromSeed,
} from '../store/calculators/FlowVesselTracker';
import { VelocityAntiAliaser } from '../store/calculators/VelocityAntiAliaser';
import { useContourContainerStore } from '../store/contours/contour-container-store';
import {
  ContourDefinitions,
  ContourLabel,
  encodeContourLabelItemId,
} from '../store/contours/contour-definitions';
import { useContourEventStore } from '../store/contours/contour-event-store';

import { useViewportStateStore } from '../store/contours/viewport-state-store';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import FlowDiagram from './FlowDiagram.vue';
import FlowOffsetThresholdTuningPanel from './FlowOffsetThresholdTuningPanel.vue';
import FlowPhantomSeries from './FlowPhantomSeries.vue';
import FlowResultTable from './FlowResultTable.vue';
import { coordinateTransformation } from './pixel-overlay/coordinateTransformation';
import useEventBus from './pixel-overlay/eventBus';
import ToolButton from './ToolButton.vue';
import VtkTwoView from './VtkTwoView.vue';

// ─── Refs & Store Init ──────────────────────────────────────────────────────

const leftRulerX = ref(0);
const rightRulerX = ref(0);

const magViewRef = ref(null);
const phaseViewRef = ref(null);
let isSyncing = false;

const flowContourLabels = [ContourLabel.FlowRoi1, ContourLabel.FlowRoi2];

const { onEvent } = useEventBus();
const magCanvasWidth = computed(() => magViewRef.value?.canvasWidth ?? 0);
const magCanvasHeight = computed(() => magViewRef.value?.canvasHeight ?? 0);

const flowStore = useFlowCalculatorStore();
const viewportStateStore = useViewportStateStore();
const currentStudyStore = useCurrentStudyStore();
const currentMagnitudeImageIndices = computed(() =>
  viewportStateStore.getSelectedImageIndices(InitViewIDs.FlowMagnitude),
);
const currentMagnitudeSeriesIndex = computed(
  () => currentMagnitudeImageIndices.value.seriesIdx,
);

const magImageUid = computed(() => {
  const indices = currentMagnitudeImageIndices.value;
  return currentStudyStore.getImageUID(indices);
});

// ─── Utility Functions (used by many handlers) ─────────────────────────────

function toRecord(buttons, defaultValue = false) {
  return buttons.reduce((acc, button) => {
    acc[button.value] = defaultValue;
    return acc;
  }, {});
}

// ─── Button Configs & Toggle State ──────────────────────────────────────────
const invertFlowButtons = flowContourLabels.map(label => ({
  icon: 'mdi mdi-swap-vertical',
  label: `Invert ${label}`,
  value: label,
}));
const displayFlowButtons = flowContourLabels.map(label => ({
  icon: 'mdi mdi-eye',
  label: `Display ${label}`,
  value: label,
}));
const correctionOptions = [
  {
    icon: 'mdi mdi-refresh-circle',
    label: 'Antialiasing',
    value: 'antialiasing',
  },
  {
    icon: 'mdi mdi-circle-double',
    label: 'Static Tissue ROI Correction',
    value: 'staticTissueRoiCorrection',
  },
  {
    icon: 'mdi mdi-square-opacity',
    label: 'Static Tissue Mask Correction',
    value: 'staticTissueMaskCorrection',
  },
  {
    icon: 'mdi mdi-adjust',
    label: 'Phantom Correction',
    value: 'phantomCorrection',
  },
];
const diagramOptions = [
  { icon: 'mdi mdi-chart-bell-curve', label: 'Flow Rate', value: 'flowRate' },
  {
    icon: 'mdi mdi-chart-line-variant',
    label: 'Velocity Mean',
    value: 'velocityMean',
  },
  { icon: 'mdi mdi-texture-box', label: 'ROI Area', value: 'roiArea' },
];
const toggleState = reactive({
  invertFlow: toRecord(invertFlowButtons, false),
  displayFlow: toRecord(displayFlowButtons, true),
  correctionOptions: toRecord(correctionOptions, false),
  diagramOptions: toRecord(diagramOptions, false),
});

function getInvertedFlowLabels() {
  return flowContourLabels.filter(label => toggleState.invertFlow[label]);
}

function recalculateFlow() {
  const contourLabels = getInvertedFlowLabels();
  flowStore.invertedFlowRois = contourLabels;
  flowStore.calculate();
}

// Mirror a single source image UID's contours onto its counterpart image in the
// opposite slice (magnitude <-> phase) at the same phase index.
function mirrorUidToCounterpart(event, sourceUID, magUIDs, phaseUIDs) {
  const magIdx = magUIDs.indexOf(sourceUID);
  const phaseIdx = phaseUIDs.indexOf(sourceUID);

  let targetUID;
  if (magIdx >= 0 && magIdx < phaseUIDs.length) {
    targetUID = phaseUIDs[magIdx];
  }
  else if (phaseIdx >= 0 && phaseIdx < magUIDs.length) {
    targetUID = magUIDs[phaseIdx];
  }
  else {
    return;
  }

  if (!targetUID) return;

  const contourStore = useContourContainerStore();
  const sourceContainer = contourStore.getContourContainer(sourceUID);
  const allContours = sourceContainer?.getDrawingContours() || {};
  const allContourLabels = Object.keys(allContours);

  if (event.type === 'delete' || event.type === 'deleteAll') {
    allContourLabels.forEach((label) => {
      const contourId = { label, id: encodeContourLabelItemId([0]) };
      contourStore.deleteContour(targetUID, contourId, false, false);
    });
  }
  else if (event.type === 'translate') {
    allContourLabels.forEach((label) => {
      if (event.label && event.label !== label) return;
      const contourId = { label, id: encodeContourLabelItemId([0]) };
      const contour = sourceContainer?.getContour(contourId);
      if (contour) {
        const polygon = contour.getPolygonScaled();
        if (polygon?.length) {
          contourStore.addContour(
            targetUID,
            polygon,
            label,
            encodeContourLabelItemId([0]),
            false,
            false,
            true,
          );
        }
      }
    });
  }
  else {
    allContourLabels.forEach((label) => {
      const contourId = { label, id: encodeContourLabelItemId([0]) };
      const contour = sourceContainer?.getContour(contourId);
      if (contour) {
        const polygon = contour.getPolygonScaled();
        if (polygon?.length) {
          contourStore.addContour(
            targetUID,
            polygon,
            label,
            encodeContourLabelItemId([0]),
            false,
            false,
            true,
          );
        }
      }
      else {
        contourStore.deleteContour(targetUID, contourId, false, false);
      }
    });
  }
}

function syncMagPhaseContours(event) {
  if (isSyncing) return;
  if (!event.UIDs.length) return;

  const { seriesIdx } = currentMagnitudeImageIndices.value;
  const magUIDs = currentStudyStore.getSliceImageIDs(seriesIdx, 1);
  const phaseUIDs = currentStudyStore.getSliceImageIDs(seriesIdx, 0);

  isSyncing = true;

  // Handle every UID in the event, not just the first: context-menu "Forward"
  // emits a single copy/batch event carrying all affected phase UIDs (and the
  // same UIDs arrive again via the batch event), so we dedupe and mirror each
  // source image to its counterpart in the opposite slice.
  [...new Set(event.UIDs)].forEach(sourceUID =>
    mirrorUidToCounterpart(event, sourceUID, magUIDs, phaseUIDs),
  );

  isSyncing = false;
}

const handleContourChange = (event) => {
  if (isSyncing) return;
  syncMagPhaseContours(event);
  recalculateFlow();
};

function getDisplayedFlowLabels() {
  return flowContourLabels.filter(label => toggleState.displayFlow[label]);
}

function getActiveDiagramValue(state) {
  if (toggleState.diagramOptions.velocityMean) {
    return {
      values: state.MeanSignalIntensities,
      yLabel: 'Mean Velocity (cm/s)',
    };
  }
  if (toggleState.diagramOptions.roiArea) {
    return { values: state.Areas, yLabel: 'Area (cm²)' };
  }
  return { values: state.FlowRates, yLabel: 'Flow Rate (ml/s)' };
}

// ─── Correction & Processing Handlers ───────────────────────────────────────

async function antialiasingHandler(reset = false) {
  console.log('antialiasing: ');

  if (reset) {
    flowStore.antialiasing = false;
    await resetFlowVelocityToBaseline();
    return;
  }

  const seriesIndices = currentMagnitudeImageIndices.value;
  const series
    = useCurrentStudyStore().activeStudy.series[seriesIndices.seriesIdx];
  if (!series?.slices?.[0]?.images?.[seriesIndices.imageIdx]) return;

  const imageUID = series.slices[0].images[seriesIndices.imageIdx].uid;
  const info = useCurrentStudyStore().imageInfoDicom[imageUID];
  if (!info) return;

  const width = info.Columns;
  const height = info.Rows;
  const venc = 4096;

  const velUids = series.slices[0].images.map(image => image.uid);
  const magUids = series.slices[0].images.map(image => image.uid);

  const velFrames = velUids.map(uid =>
    useCurrentStudyStore()
      .getVtkImageData(uid)
      ?.getPointData()
      .getScalars()
      .getData(),
  );
  const magFrames = magUids.map(uid =>
    useCurrentStudyStore()
      .getVtkImageData(uid)
      ?.getPointData()
      .getScalars()
      .getData(),
  );

  const unwrapper = new VelocityAntiAliaser({ width, height, venc });
  unwrapper.setTemporalMode('cyclic');

  const unwrappedFrames = unwrapper.unwrapFrames(velFrames, magFrames);
  // const results = unwrappedFrames.map((corrected) => ({ corrected }));

  unwrappedFrames.forEach((correctedFrame, frameIndex) => {
    const uid = velUids[frameIndex];
    if (uid && correctedFrame) {
      const success = currentStudyStore.modifyVtkImageData(
        uid,
        correctedFrame,
        `Anti-aliasing correction applied (frame ${frameIndex})`,
      );

      if (!success) {
        console.warn(`Failed to update image ${uid} with corrected data`);
      }
    }
  });
  flowStore.antialiasing = true;
  flowStore.resetContourSignalIntensityStatistic();
  recalculateFlow();
  console.log(
    `Updated ${unwrappedFrames.length} frames with anti-aliased velocity data`,
  );
}

// ─── Phase Offset Threshold ─────────────────────────────────────────────────

const showThresholdTuning = ref(false);
const currentOffsetThreshold = ref(5.0);
const thresholdTuningData = ref({
  phaseFrames: [],
  magnitudeFrames: [],
  width: 0,
  height: 0,
  venc: 150,
});

function openThresholdTuning() {
  const phaseIndices = viewportStateStore.getSelectedImageIndices(
    InitViewIDs.FlowPhase,
  );
  const magIndices = viewportStateStore.getSelectedImageIndices(
    InitViewIDs.FlowMagnitude,
  );

  const phaseSeries
    = currentStudyStore.activeStudy.series[phaseIndices.seriesIdx];
  if (
    !phaseSeries?.slices?.[phaseIndices.sliceIdx]?.images?.[
      phaseIndices.imageIdx
    ]
  ) {
    return;
  }

  const refImageUID
    = phaseSeries.slices[phaseIndices.sliceIdx].images[phaseIndices.imageIdx].uid;
  const refInfo = currentStudyStore.imageInfoDicom[refImageUID];
  if (!refInfo) return;

  const width = refInfo.Columns;
  const height = refInfo.Rows;
  const venc = refInfo.Venc2D || 150;

  const phaseSliceUIDs = currentStudyStore.getSliceImageUIDs(
    phaseIndices.seriesIdx,
    phaseIndices.sliceIdx,
  );
  const phaseFramesRaw = phaseSliceUIDs
    .map(uid =>
      currentStudyStore
        .getVtkImageData(uid)
        ?.getPointData()
        .getScalars()
        .getData(),
    )
    .filter(data => !!data);

  const phaseFrames = phaseFramesRaw.map((frame) => {
    const converted = new Float32Array(frame.length);
    for (let i = 0; i < frame.length; i++) {
      converted[i] = (frame[i] / 4096) * venc;
    }
    return converted;
  });

  const magSliceUIDs = currentStudyStore.getSliceImageUIDs(
    magIndices.seriesIdx,
    magIndices.sliceIdx,
  );
  const magFrames = magSliceUIDs
    .map(uid =>
      currentStudyStore
        .getVtkImageData(uid)
        ?.getPointData()
        .getScalars()
        .getData(),
    )
    .filter(data => !!data);

  if (!phaseFrames.length || !magFrames.length) {
    console.error('No phase or magnitude frames available');
    return;
  }

  thresholdTuningData.value = {
    phaseFrames,
    magnitudeFrames: magFrames,
    width,
    height,
    venc,
  };

  showThresholdTuning.value = true;
}

function runOffsetCorrectionWithThreshold(threshold, polynomialOrder = 2) {
  const seriesIndices = currentMagnitudeImageIndices.value;
  const series = currentStudyStore.activeStudy.series[seriesIndices.seriesIdx];
  if (!series?.slices?.[0]?.images?.length) return;

  const velUids = series.slices[0].images.map(image => image.uid);

  const imageUID = velUids[0];
  const info = currentStudyStore.imageInfoDicom[imageUID];
  if (!info) return;

  const width = info.Columns;
  const height = info.Rows;
  const venc = info.Venc2D / info.VencScale2D;

  const velFrames = velUids.map(uid =>
    currentStudyStore
      .getVtkImageData(uid)
      ?.getPointData()
      .getScalars()
      .getData(),
  );
  const magUids = series.slices[1]?.images?.map(image => image.uid) || [];
  const magFrames = magUids.map(uid =>
    currentStudyStore
      .getVtkImageData(uid)
      ?.getPointData()
      .getScalars()
      .getData(),
  );

  if (!velFrames.length || velFrames.some(f => !f)) {
    console.error('No frames available for correction');
    return;
  }

  const corrector = new FlowPhaseOffsetCorrection({
    width,
    height,
    venc,
    polynomialOrder,
    thresholds: {
      magnitudePercentile: 0.99,
      temporalStdThreshold: threshold,
      minPixelCount: 30,
    },
  });

  const result = corrector.correctPhaseOffset(velFrames, magFrames);

  result.correctedFrames.forEach((correctedFrame, frameIndex) => {
    const uid = velUids[frameIndex];
    if (uid && correctedFrame) {
      const success = currentStudyStore.modifyVtkImageData(
        uid,
        correctedFrame,
        `Phase offset correction (frame ${frameIndex})`,
      );
      if (!success) {
        console.warn(`Failed to update image ${uid} with corrected data`);
      }
    }
  });

  flowStore.staticTissueMaskCorrection = true;
  flowStore.resetContourSignalIntensityStatistic();
  recalculateFlow();

  console.log(
    `Updated ${result.correctedFrames.length} frames with offset-corrected data`,
  );
}

function applyOffsetThreshold({ threshold, polynomialOrder }) {
  console.log('Applying offset correction with threshold:', threshold, 'cm/s');
  currentOffsetThreshold.value = threshold;
  runOffsetCorrectionWithThreshold(threshold, polynomialOrder);
}

const showPhantomDrop = ref(false);
// Hydrate from the store so remounting the flow component restores the
// previously selected phantom series (and can re-apply the correction).
const phantomSeriesIdx = ref(flowStore.phantomSeriesIdx);
toggleState.correctionOptions.antialiasing = flowStore.antialiasing;
toggleState.correctionOptions.phantomCorrection = flowStore.phantomCorrection;
// Same for static-tissue ROI correction: the store flag survives remount and
// drives calculateRoi, so the button must match or correction appears to run
// "regardless of button state".
toggleState.correctionOptions.staticTissueRoiCorrection
  = flowStore.staticTissueRoiCorrection;
toggleState.correctionOptions.staticTissueMaskCorrection
  = flowStore.staticTissueMaskCorrection;
let isReapplyingCorrections = false;

// Phantom background correction is owned by FlowCalculatorStore. The component
// keeps only the UI/lifecycle bits (showPhantomDrop ref, the drop target and
// watcher) and drives the recalc + repaint. Enabled state + phantomSeriesIdx
// live in the store so they survive leave/re-enter.
async function phantomCorrectionHandler(reset = false) {
  if (reset || phantomSeriesIdx.value < 0) {
    flowStore.clearPhantomCorrection();
    await resetFlowVelocityToBaseline();
    return;
  }

  // Rebuild from baseline first so changing phantom series (or re-applying
  // after remount) never double-subtracts on already-corrected cache.
  await flowStore.resetFlowVelocityToBaseline(
    currentMagnitudeImageIndices.value.seriesIdx,
  );

  const applied = await flowStore.applyPhantomCorrection(
    currentMagnitudeImageIndices.value.seriesIdx,
    phantomSeriesIdx.value,
  );
  if (applied) {
    recalculateFlow();
    renderFlowViews();
  }
  else {
    flowStore.clearPhantomCorrection();
    toggleState.correctionOptions.phantomCorrection = false;
  }
}

async function reapplyActiveImageCorrections() {
  isReapplyingCorrections = true;
  try {
    await applyGeMagnitudeNormalization();
    if (flowStore.phantomCorrection && flowStore.phantomSeriesIdx >= 0) {
      phantomSeriesIdx.value = flowStore.phantomSeriesIdx;
      toggleState.correctionOptions.phantomCorrection = true;
      // Cache was restored to original on unmount; apply GE then phantom once.
      const applied = await flowStore.applyPhantomCorrection(
        currentMagnitudeImageIndices.value.seriesIdx,
        flowStore.phantomSeriesIdx,
      );
      if (applied) renderFlowViews();
    }
    else {
      toggleState.correctionOptions.phantomCorrection = false;
    }
  }
  finally {
    isReapplyingCorrections = false;
  }
}

function renderFlowViews() {
  magViewRef.value?.viewProxy?.renderLater();
  phaseViewRef.value?.viewProxy?.renderLater();
}

// GE magnitude normalization is owned by FlowCalculatorStore as a base
// correction layer (see the store for the full rationale). The component only
// drives lifecycle (enter/leave/series change) and the view repaint, since the
// view proxies live here.
async function applyGeMagnitudeNormalization() {
  const applied = await flowStore.applyGeMagnitudeNormalization(
    currentMagnitudeImageIndices.value.seriesIdx,
  );
  if (applied) renderFlowViews();
}

// Restore every normalized series to its original data (used on unmount so the
// shared, global image cache is handed back exactly as it was found).
function restoreAllGeMagnitudeNormalization() {
  if (flowStore.restoreAllGeMagnitudeNormalization()) renderFlowViews();
}

// Reset the current flow series' velocity data to its baseline (original image
// with the mandatory GE normalization re-applied). Used by the other
// corrections when they are toggled off, so turning one correction off never
// silently drops the GE normalization base layer.
async function resetFlowVelocityToBaseline() {
  flowStore.antialiasing = false;
  flowStore.staticTissueMaskCorrection = false;
  await flowStore.resetFlowVelocityToBaseline(
    currentMagnitudeImageIndices.value.seriesIdx,
  );
  recalculateFlow();
  renderFlowViews();
}

const showOverlay = ref(false);

function handleToggleOverlay() {
  showOverlay.value = !showOverlay.value;
  if (showOverlay.value) {
    flowStore.setOverlayDataForAllPhases();
  }
  else {
    flowStore.resetOverlayData();
  }
  magViewRef.value?.viewProxy?.renderLater();
}

// function staticTissueRoiCorrectionHandler() {
//   console.log('staticTissueRoiCorrection: ');
// }

// function staticTissueMaskCorrectionHandler() {
//   console.log('staticTissueMaskCorrection: ');
// }

// ─── Toggle Group Handlers ──────────────────────────────────────────────────

function invertFlowHandler() {
  recalculateFlow();
}

function displayFlowHandler(label) {
  console.log('displayFlow: ', label);
}

function correctionOptionsHandler(label) {
  console.log('correctionOptions: ', label);

  if (label === 'antialiasing') {
    if (toggleState.correctionOptions[label]) {
      antialiasingHandler();
    }
    else {
      antialiasingHandler(true);
    }
  }

  if (label === 'staticTissueRoiCorrection') {
    if (toggleState.correctionOptions[label]) {
      const { seriesIdx } = currentMagnitudeImageIndices.value;
      if (!flowStore.hasStaticTissueRoiInAllPhases(seriesIdx)) {
        useAlert().warning(
          'Static Tissue ROI correction requires a static tissue ROI on every phase.',
        );
        toggleState.correctionOptions[label] = false;
        flowStore.staticTissueRoiCorrection = false;
        return;
      }
      flowStore.staticTissueRoiCorrection = true;
    }
    else {
      flowStore.staticTissueRoiCorrection = false;
    }
    recalculateFlow();
  }

  if (label === 'staticTissueMaskCorrection') {
    if (toggleState.correctionOptions[label]) {
      openThresholdTuning();
    }
    else {
      resetFlowVelocityToBaseline();
    }
  }
  if (label === 'phantomCorrection') {
    if (toggleState.correctionOptions[label]) {
      showPhantomDrop.value = true;
      if (phantomSeriesIdx.value >= 0) {
        phantomCorrectionHandler();
      }
    }
    else {
      showPhantomDrop.value = false;
      phantomCorrectionHandler(true);
    }
  }
}

function diagramOptionsHandler(label) {
  console.log('diagramOptions: ', label);
}

function handleToggle(group, button) {
  const isExclusive = group === 'diagramOptions';

  if (isExclusive) {
    Object.keys(toggleState[group]).forEach((key) => {
      toggleState[group][key] = false;
    });
    toggleState[group][button.value] = true;
  }
  else {
    toggleState[group][button.value] = !toggleState[group][button.value];
  }

  if (group === 'invertFlow') {
    invertFlowHandler();
  }
  else if (group === 'displayFlow') {
    displayFlowHandler(button.value);
  }
  else if (group === 'correctionOptions') {
    correctionOptionsHandler(button.value);
  }
  else if (group === 'diagramOptions') {
    diagramOptionsHandler(button.value);
  }
}

// ─── Track Vessels ──────────────────────────────────────────────────────────

function trackVessels() {
  isSyncing = true;
  const refImageIndices = viewportStateStore.getSelectedImageIndices(
    InitViewIDs.FlowMagnitude,
  );

  const refImageUID
    = currentStudyStore.activeStudy.series[refImageIndices.seriesIdx].slices[
      refImageIndices.sliceIdx
    ].images[refImageIndices.imageIdx].uid;
  const sliceImageUIDs = currentStudyStore.getSliceImageUIDs(
    refImageIndices.seriesIdx,
    1,
  );
  const phaseSliceImageIDs = currentStudyStore.getSliceImageUIDs(
    refImageIndices.seriesIdx,
    0,
  );
  const refImageContours
    = useContourContainerStore().getContourContainer(refImageUID);
  if (refImageContours) {
    let activeLabel = viewportStateStore.getDrawingContour(
      InitViewIDs.FlowMagnitude,
    );
    if (
      viewportStateStore.getToolMode(InitViewIDs.FlowMagnitude)
      !== ViewportToolMode.Contour
    ) {
      activeLabel = null;
    }
    const labelsToTrack = flowContourLabels.includes(activeLabel)
      ? [activeLabel]
      : flowContourLabels;

    labelsToTrack.every((contourLabel) => {
      const contour = refImageContours.getContour({
        label: contourLabel,
        id: encodeContourLabelItemId([0]),
      });
      if (contour) {
        const polygon = contour.getPolygonScaled();
        console.log('polygon: ', polygon);
        const dimensions = currentStudyStore
          .getVtkImageData(refImageUID)
          .getDimensions();
        const frames = sliceImageUIDs
          .map(uid =>
            currentStudyStore
              .getVtkImageData(uid)
              .getPointData()
              .getScalars()
              .getData(),
          )
          .filter(imageData => !!imageData);

        if (!frames.length) return false;

        const tracker = new FlowVesselTracker(
          frames,
          dimensions[0],
          dimensions[1],
          {
            bitDepth: 16,
            normalize: true,
          },
        );
        const trackedPolygons = tracker.track(polygon, {
          refIndex: refImageIndices.imageIdx,
          searchRadius: 5,
          similarityMetric: 'ncc',
          enableShapeAdjustment: true,
          displacementPenaltyLambda: 0.01,
          vertexPatchRadius: 2,
          vertexSearchRadius: 1,
          vertexStep: 0.25,
          smoothShape: true,
          smoothIterations: 20,
        });

        trackedPolygons.forEach((trackedPoly, frameIdx) => {
          const targetUID = sliceImageUIDs[frameIdx];
          const phaseTargetUID = phaseSliceImageIDs[frameIdx];
          if (!targetUID || !phaseTargetUID || !trackedPoly?.length) return;

          useContourContainerStore().addContour(
            targetUID,
            trackedPoly,
            contourLabel,
            encodeContourLabelItemId([0]),
            false,
            false,
            true,
          );
          useContourContainerStore().addContour(
            phaseTargetUID,
            trackedPoly,
            contourLabel,
            encodeContourLabelItemId([0]),
            false,
            false,
            true,
          );
        });
      }
      return true;
    });
  }
  recalculateFlow();
  isSyncing = false;
}

// ─── Scroll Arrows & Phantom Positioning ────────────────────────────────────

const flowStripRef = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const phantomBtnRef = ref(null);
const phantomPopLeft = ref(0);
function updatePhantomPopPosition() {
  const refVal = phantomBtnRef.value;
  const btnEl = Array.isArray(refVal) ? refVal[0] : refVal;
  const wrapperEl = btnEl?.closest('.flow-controls-wrapper');
  if (!btnEl || !wrapperEl) return;
  const btnRect = btnEl.getBoundingClientRect();
  const wrapperRect = wrapperEl.getBoundingClientRect();
  phantomPopLeft.value = btnRect.left + btnRect.width / 2 - wrapperRect.left;
}

function updateScrollArrows() {
  const el = flowStripRef.value;
  if (!el) return;
  canScrollLeft.value = el.scrollLeft > 0;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  updatePhantomPopPosition();
}

function scrollStripLeft() {
  flowStripRef.value?.scrollBy({ left: -120, behavior: 'smooth' });
}

function scrollStripRight() {
  flowStripRef.value?.scrollBy({ left: 120, behavior: 'smooth' });
}

// ─── Overlay Slider ─────────────────────────────────────────────────────────

function onOverlaySliderInput(e) {
  flowStore.setOverlayNoiseThreshold(Number(e.target.value));
  flowStore.setOverlayData(currentMagnitudeImageIndices.value.imageIdx);
  magViewRef.value?.viewProxy?.renderLater();
}

function onOverlaySliderReleased() {
  flowStore.setOverlayDataForAllPhases();
  magViewRef.value?.viewProxy?.renderLater();
}

// ─── Phantom Correction UI ──────────────────────────────────────────────────

const phantomDropVisible = computed(() => {
  if (!toggleState.correctionOptions.phantomCorrection) return false;
  return showPhantomDrop.value;
});

let phantomHoverTimer = null;

function onPhantomHover() {
  if (
    toggleState.correctionOptions.phantomCorrection
    && !showPhantomDrop.value
  ) {
    phantomHoverTimer = setTimeout(() => {
      showPhantomDrop.value = true;
      phantomHoverTimer = null;
    }, 3000);
  }
}

function onPhantomHoverLeave() {
  if (phantomHoverTimer) {
    clearTimeout(phantomHoverTimer);
    phantomHoverTimer = null;
  }
}

function onStripDragOver() {
  if (
    toggleState.correctionOptions.phantomCorrection
    && !showPhantomDrop.value
  ) {
    showPhantomDrop.value = true;
  }
}

// ─── Computed Properties ────────────────────────────────────────────────────

const timeMarker = computed(() => {
  const currentImageIndices = viewportStateStore.getSelectedImageIndices(
    InitViewIDs.FlowPhase,
  );
  const currentImageUid = currentStudyStore.getImageUID(currentImageIndices);
  if (!currentImageUid) return 0;
  return currentStudyStore.imageInfoDicom[currentImageUid]?.TriggerTime || 0;
});

const activeDiagramLabel = computed(() => {
  if (toggleState.diagramOptions.velocityMean) return 'Mean Velocity (cm/s)';
  if (toggleState.diagramOptions.roiArea) return 'Area (cm²)';
  return 'Flow Rate (ml/s)';
});

const activeDiagramTitle = computed(() => {
  if (toggleState.diagramOptions.velocityMean) return 'Velocity Mean Over Time';
  if (toggleState.diagramOptions.roiArea) return 'ROI Area Over Time';
  return 'Flow Rate Over Time';
});

const flowData = computed(() => {
  return Object.entries(flowStore.flowAnalysisStates)
    .map(([key, state], idx) => {
      if (!getDisplayedFlowLabels().includes(key)) return null;

      const { values } = getActiveDiagramValue(state);
      const samples = state.TriggerTimes.map((time, phaseIdx) => ({
        time,
        flow: values[phaseIdx] ?? 0,
      }));
      if (!samples.length) return null;

      // Use the same color as the drawn contour so diagram and viewer match.
      const color
        = ContourDefinitions[key]?.color
          ?? ContourDefinitions[ContourLabel.FlowRoi1].color;

      return {
        id: key || `ROI ${idx + 1}`,
        color,
        data: samples,
      };
    })
    .filter(series => !!series);
});

// Net volume inside / outside the ruler window for every present flow ROI
// (not filtered by display toggles). Outside = total − inside.
const rulerVolumeSummary = computed(() => {
  return Object.entries(flowStore.flowAnalysisStates)
    .filter(([key]) => flowContourLabels.includes(key))
    .map(([key, state]) => {
      const inside = state.RulerTotalVolume ?? 0;
      const outside = (state.TotalVolume ?? 0) - inside;
      return {
        id: ContourDefinitions[key]?.displayLabel ?? key,
        color:
          ContourDefinitions[key]?.color
          ?? ContourDefinitions[ContourLabel.FlowRoi1].color,
        inside,
        outside,
      };
    })
    // Hide when rulers cover the full cycle (nothing outside).
    .filter(entry => Math.abs(entry.outside) > 0);
});

function syncRulersToStore() {
  const left = Math.min(leftRulerX.value, rightRulerX.value);
  const right = Math.max(leftRulerX.value, rightRulerX.value);
  const { rulerRange } = flowStore;
  if (
    rulerRange.left === left
    && rulerRange.right === right
  ) {
    return;
  }
  flowStore.rulerRange = {
    left,
    right,
    delta: right - left,
  };
  recalculateFlow();
}

function initRulersFromAnalysis() {
  const states = Object.values(flowStore.flowAnalysisStates).filter(
    s => s.TriggerTimes?.length > 1,
  );
  if (!states.length) return;

  const times = states.flatMap(s => s.TriggerTimes);
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  if (!Number.isFinite(minT) || !Number.isFinite(maxT) || minT === maxT) {
    return;
  }

  // Only auto-span when rulers are still at the unset default.
  if (leftRulerX.value === 0 && rightRulerX.value === 0) {
    leftRulerX.value = minT;
    rightRulerX.value = maxT;
  }
}

const flowResults = computed(() => {
  // Stable ROI slots — never use Object.values() (a forwarded static-tissue
  // analysis used to appear as "Flow ROI 2" and look like values changed).
  return flowContourLabels.map((label, idx) => {
    const state = flowStore.flowAnalysisStates[label];
    if (!state) return null;
    return {
      title: `Flow ROI ${idx + 1}`,
      TotalForwardVolume: state.TotalForwardVolume,
      TotalBackwardVolume: state.TotalBackwardVolume,
      TotalVolume: state.TotalVolume,
      RegurgitationFraction: state.RegurgitationFraction,
      VolPerMin: state.VolPerMin,
      VolPerMinEffective: state.VolPerMinEffective,
      HeartRate: state.HeartRate,
      MaxPressureGradient: state.MaxPressureGradient,
      MeanPressureGradient: state.MeanPressureGradient,
      MaximumVelocity: state.MaximumVelocity,
      MinimumVelocity: state.MinimumVelocity,
      MaximumAcceleration: state.MaximumAcceleration,
      MinimumAcceleration: state.MinimumAcceleration,
      MaximumFlow: state.MaximumFlow,
      MinimumFlow: state.MinimumFlow,
      RulerForwardVolume: state.RulerForwardVolume,
      RulerBackwardVolume: state.RulerBackwardVolume,
      RulerTotalVolume: state.RulerTotalVolume,
    };
  });
});

const trackContoursButtons = [
  {
    icon: 'mdi mdi-shape-circle-plus',
    label: 'Track Vessels',
    onClick: () => trackVessels(),
  },
];

// ─── Seed Segmentation ──────────────────────────────────────────────────────

const { getCurrentCoordinates } = coordinateTransformation({
  currentImageUid: magImageUid,
  id: InitViewIDs.FlowMagnitude,
  canvasWidth: magCanvasWidth,
  canvasHeight: magCanvasHeight,
});

// ─── Watchers ───────────────────────────────────────────────────────────────

watch(phantomDropVisible, (visible) => {
  if (visible) nextTick(() => updatePhantomPopPosition());
});

watch(
  currentMagnitudeSeriesIndex,
  async (newIdx, oldIdx) => {
    console.log(
      'currentMagnitudeSeriesIndex: ',
      currentMagnitudeSeriesIndex.value,
    );
    // Reset rulers so they re-span the new series' trigger times after calc.
    leftRulerX.value = 0;
    rightRulerX.value = 0;
    flowStore.rulerRange = { left: 0, right: 0, delta: 0 };

    // Changing the flow series invalidates the previous phantom pairing and
    // static-tissue ROI correction (those ROIs belong to the prior series).
    // Remount (oldIdx undefined) keeps persisted flags so they can re-apply.
    if (oldIdx !== undefined && oldIdx !== newIdx) {
      isReapplyingCorrections = true;
      flowStore.clearPhantomCorrection();
      toggleState.correctionOptions.phantomCorrection = false;
      phantomSeriesIdx.value = -1;
      flowStore.staticTissueRoiCorrection = false;
      toggleState.correctionOptions.staticTissueRoiCorrection = false;
      isReapplyingCorrections = false;
    }

    // Re-establish GE base + any persisted phantom correction after series
    // change / remount (unmount restores the shared image cache to original).
    await reapplyActiveImageCorrections();
    recalculateFlow();
  },
  { immediate: true },
);

watch([leftRulerX, rightRulerX], () => {
  syncRulersToStore();
});

watch(
  () => flowStore.flowAnalysisStates,
  () => {
    initRulersFromAnalysis();
  },
  { deep: true },
);

watch(phantomSeriesIdx, () => {
  if (isReapplyingCorrections) return;
  if (phantomSeriesIdx.value >= 0) {
    phantomCorrectionHandler();
  }
  else {
    phantomCorrectionHandler(true);
  }
});

// Keep the toggle button in sync when the store auto-disables the static-tissue
// ROI correction (e.g. an ROI became missing on a phase during a recalc).
watch(
  () => flowStore.staticTissueRoiCorrection,
  (enabled) => {
    toggleState.correctionOptions.staticTissueRoiCorrection = enabled;
  },
  { immediate: true },
);

watch(
  currentMagnitudeSeriesIndex,
  () => {
    // ToDo
  },
  { immediate: true },
);

watch(
  currentMagnitudeImageIndices,
  () => {
    // ToDo
  },
  { immediate: true },
);

// ─── Event Listeners ────────────────────────────────────────────────────────

const unsub = useContourEventStore().on((event) => {
  handleContourChange(event);
});

onEvent('mouseclick', (e) => {
  const magEl = magViewRef.value?.$el;
  if (!magEl || !magEl.contains(e.target)) return;
  const activeLabel = viewportStateStore.getDrawingContour(
    InitViewIDs.FlowMagnitude,
  );
  const contourLabel = flowContourLabels.includes(activeLabel)
    ? activeLabel
    : undefined;
  if (!contourLabel) return;
  if (!magImageUid.value) return;
  const existingContainer = useContourContainerStore().getContourContainer(
    magImageUid.value,
  );
  if (
    existingContainer?.getContour({
      label: contourLabel,
      id: encodeContourLabelItemId([0]),
    })
  ) {
    return;
  }

  const [imageX, imageY] = getCurrentCoordinates(e);
  if (
    !imageX
    || !imageY
    || !Number.isFinite(imageX)
    || !Number.isFinite(imageY)
  ) {
    return;
  }

  const vtkImage = currentStudyStore.getVtkImageData(magImageUid.value);
  if (!vtkImage) return;

  const frame = vtkImage.getPointData().getScalars().getData();
  const dims = vtkImage.getDimensions();

  const contour = segmentVesselFromSeed(
    frame,
    dims[0],
    dims[1],
    Math.round(imageX),
    Math.round(imageY),
    {
      numRays: 30,
      maxRadius: 20,
      useGradientEdge: true,
      intensityDropThreshold: 0.5,
      smoothIterations: 3,
    },
  );

  if (!contour?.length) return;

  contour.forEach((pt) => {
    pt.x += 0.5;
    pt.y += 0.5;
  });

  useContourContainerStore().addContour(
    magImageUid.value,
    contour,
    contourLabel,
    encodeContourLabelItemId([0]),
    true,
    false,
    true,
  );
});

// ─── Lifecycle ──────────────────────────────────────────────────────────────

let stripResizeObserver = null;

onMounted(() => {
  nextTick(() => {
    updateScrollArrows();
    if (flowStripRef.value) {
      stripResizeObserver = new ResizeObserver(() => updateScrollArrows());
      stripResizeObserver.observe(flowStripRef.value);
    }
  });
  const flowContourLabels = [
    ContourLabel.FlowRoi1,
    ContourLabel.FlowRoi2,
    ContourLabel.FlowStaticTissueRoi,
    ContourLabel.GeneralPolygon,
    ContourLabel.GeneralLine,
    ContourLabel.AnnotationArrow,
    ContourLabel.AnnotationText,
  ];
  ['FlowMagnitude', 'FlowPhase'].forEach(viewId =>
    viewportStateStore.setAllowedContourLabels(viewId, flowContourLabels),
  );
});

onBeforeUnmount(() => {
  stripResizeObserver?.disconnect();
});

// Hand the shared image cache back untouched: restore every velocity series we
// magnitude-normalized / phantom-corrected while the flow component was active,
// so the original image data is preserved for the rest of the app. Persisted
// correction flags stay in the store so re-entering flow can re-apply them.
onBeforeUnmount(() => {
  flowStore.updateSeriesIndices();
  restoreAllGeMagnitudeNormalization();
});

onBeforeUnmount(unsub);

onBeforeUnmount(() => {
  if (phantomHoverTimer) clearTimeout(phantomHoverTimer);
});

// ─── Camera Sync ────────────────────────────────────────────────────────────

let syncCameras = false;
let bothReady = false;
let unsubMag = null;
let unsubPhase = null;

onMounted(() => {
  const setupCameraSync = () => {
    const magProxy = magViewRef.value?.viewProxy;
    const phaseProxy = phaseViewRef.value?.viewProxy;
    if (!magProxy || !phaseProxy) return false;

    const magCamera = magProxy.getCamera();
    const phaseCamera = phaseProxy.getCamera();

    const stopWatcher = watch(
      [
        () =>
          currentStudyStore.getVtkImageDataIndex(
            viewportStateStore.getSelectedImageIndices(InitViewIDs.FlowMagnitude),
            InitViewIDs.FlowMagnitude,
          ),
        () =>
          currentStudyStore.getVtkImageDataIndex(
            viewportStateStore.getSelectedImageIndices(InitViewIDs.FlowPhase),
            InitViewIDs.FlowPhase,
          ),
      ],
      ([magData, phaseData]) => {
        if (magData && phaseData && !bothReady) {
          bothReady = true;
          nextTick(() => stopWatcher());
          syncCameras = true;
          phaseCamera.setParallelScale(magCamera.getParallelScale());
          phaseCamera.setPosition(...magCamera.getPosition());
          phaseCamera.setFocalPoint(...magCamera.getFocalPoint());
          phaseProxy.renderLater();
          syncCameras = false;
        }
      },
      { immediate: true },
    );

    unsubMag = magCamera.onModified(() => {
      if (!bothReady || syncCameras) return;
      syncCameras = true;
      phaseCamera.setParallelScale(magCamera.getParallelScale());
      phaseCamera.setPosition(...magCamera.getPosition());
      phaseCamera.setFocalPoint(...magCamera.getFocalPoint());
      phaseProxy.renderLater();
      syncCameras = false;
    });

    unsubPhase = phaseCamera.onModified(() => {
      if (!bothReady || syncCameras) return;
      syncCameras = true;
      magCamera.setParallelScale(phaseCamera.getParallelScale());
      magCamera.setPosition(...phaseCamera.getPosition());
      magCamera.setFocalPoint(...phaseCamera.getFocalPoint());
      magProxy.renderLater();
      syncCameras = false;
    });

    return true;
  };

  const stopProxyWatch = watch(
    [() => magViewRef.value?.viewProxy, () => phaseViewRef.value?.viewProxy],
    ([m, p]) => {
      if (m && p) {
        if (setupCameraSync()) {
          nextTick(() => stopProxyWatch?.());
        }
      }
    },
    { immediate: true },
  );
});

onBeforeUnmount(() => {
  unsubMag?.unsubscribe();
  unsubPhase?.unsubscribe();
});
</script>

<template>
  <div class="vtk-two-view-container">
    <div class="flex flex-1 gap-2">
      <div class="viewport-column">
        <div class="vtk-container">
          <VtkTwoView
            :id="InitViewIDs.FlowMagnitude"
            ref="magViewRef"
            class="vtk-view"
            title="Magnitude"
          />
        </div>
        <div
          v-show="showOverlay"
          class="overlay-threshold-slider simple-panel flex align-center px-3"
        >
          <input
            type="range"
            aria-label="Overlay noise threshold"
            :value="flowStore.overlayNoiseThreshold"
            min="0"
            max="100"
            step="1"
            @input="onOverlaySliderInput($event)"
            @change="onOverlaySliderReleased()"
          >
        </div>
        <div class="vtk-container">
          <VtkTwoView
            :id="InitViewIDs.FlowPhase"
            ref="phaseViewRef"
            class="vtk-view"
            title="Phase"
          />
        </div>
      </div>
      <div class="right-column flex flex-column gap-2">
        <div class="diagram-section flex flex-column gap-2">
          <div class="diagram-wrapper">
            <FlowDiagram
              v-model:left-ruler-x="leftRulerX"
              v-model:right-ruler-x="rightRulerX"
              :title="activeDiagramTitle"
              :data="flowData"
              :ruler-volume-summary="rulerVolumeSummary"
              x-label="Time (ms)"
              :y-label="activeDiagramLabel"
              :color="ContourDefinitions[ContourLabel.FlowRoi1].color"
              :show-points="true"
              :show-grid="true"
              :marker-x="timeMarker"
            />
          </div>
        </div>
        <div class="flow-controls-wrapper" @dragover="onStripDragOver">
          <button
            v-show="canScrollLeft"
            class="scroll-arrow scroll-arrow-left"
            @click="scrollStripLeft"
          >
            <i class="mdi mdi-chevron-left" />
          </button>
          <div
            ref="flowStripRef"
            class="flow-control-strip simple-panel flex align-center gap-2 h-[40px] py-2"
            @scroll="updateScrollArrows"
          >
            <div class="category-tools">
              <ToolButton
                v-for="button in trackContoursButtons"
                :key="button.label"
                :size="40"
                :icon="button.icon"
                :label="button.label"
                tooltip-location="bottom"
                @click="button.onClick"
              />
            </div>

            <div class="vertical-divider" />

            <div class="category-tools">
              <ToolButton
                v-for="(button, idx) in invertFlowButtons"
                :key="button.label"
                :size="40"
                :icon="button.icon"
                :label="button.label"
                tooltip-location="bottom"
                :class="{ 'tool-active': toggleState.invertFlow[button.value] }"
                @click="handleToggle('invertFlow', button)"
              >
                <i class="mdi mdi-chart-bell-curve stacked-icon" />
                <span class="button-index">{{ idx + 1 }}</span>
              </ToolButton>
            </div>

            <div class="vertical-divider" />

            <div class="category-tools">
              <ToolButton
                v-for="(button, idx) in displayFlowButtons"
                :key="button.label"
                :size="40"
                :icon="button.icon"
                :label="button.label"
                tooltip-location="bottom"
                :class="{
                  'tool-active': toggleState.displayFlow[button.value],
                }"
                @click="handleToggle('displayFlow', button)"
              >
                <span class="button-index">{{ idx + 1 }}</span>
              </ToolButton>
            </div>

            <div class="vertical-divider" />

            <div class="category-tools">
              <template v-for="button in correctionOptions" :key="button.label">
                <div
                  v-if="button.value === 'phantomCorrection'"
                  ref="phantomBtnRef"
                  class="phantom-btn-anchor"
                  @mouseenter="onPhantomHover"
                  @mouseleave="onPhantomHoverLeave"
                  @focusin="onPhantomHover"
                  @focusout="onPhantomHoverLeave"
                >
                  <ToolButton
                    :size="40"
                    :icon="button.icon"
                    :label="button.label"
                    tooltip-location="bottom"
                    :class="{
                      'tool-active':
                        toggleState.correctionOptions[button.value],
                    }"
                    @click="handleToggle('correctionOptions', button)"
                  />
                </div>

                <ToolButton
                  v-else
                  :size="40"
                  :icon="button.icon"
                  :label="button.label"
                  tooltip-location="bottom"
                  :class="{
                    'tool-active': toggleState.correctionOptions[button.value],
                  }"
                  @click="handleToggle('correctionOptions', button)"
                />
              </template>
            </div>

            <div class="vertical-divider" />

            <div class="category-tools">
              <ToolButton
                v-for="button in diagramOptions"
                :key="button.label"
                :size="40"
                :icon="button.icon"
                :label="button.label"
                tooltip-location="bottom"
                :class="{
                  'tool-active': toggleState.diagramOptions[button.value],
                }"
                @click="handleToggle('diagramOptions', button)"
              />
            </div>

            <div class="vertical-divider" />

            <div class="category-tools">
              <ToolButton
                :size="40"
                icon="mdi mdi-layers-outline"
                label="Toggle Velocity Overlay"
                tooltip-location="bottom"
                :class="{ 'tool-active': showOverlay }"
                @click="handleToggleOverlay"
              />
            </div>
          </div>
          <button
            v-show="canScrollRight"
            class="scroll-arrow scroll-arrow-right"
            @click="scrollStripRight"
          >
            <i class="mdi mdi-chevron-right" />
          </button>
          <!-- Phantom popover: outside the scrollable strip -->
          <Transition name="phantom-pop">
            <FlowPhantomSeries
              v-if="phantomDropVisible"
              v-model:series-idx="phantomSeriesIdx"
              class="phantom-popover"
              :style="{ left: `${phantomPopLeft}px` }"
              @hide="showPhantomDrop = false"
            />
          </Transition>
        </div>
        <div class="report-section simple-panel">
          <div class="flow-tables-container">
            <div class="tables-section">
              <FlowResultTable
                v-if="flowResults[0]"
                :results="flowResults[0]"
                title="Flow ROI 1"
              />
              <FlowResultTable
                v-if="flowResults[1]"
                :results="flowResults[1]"
                title="Flow ROI 2"
              />
            </div>
          </div>
          <slot name="report" />
        </div>
      </div>
    </div>
    <FlowOffsetThresholdTuningPanel
      :show="showThresholdTuning"
      :phase-frames="thresholdTuningData.phaseFrames"
      :magnitude-frames="thresholdTuningData.magnitudeFrames"
      :image-width="thresholdTuningData.width"
      :image-height="thresholdTuningData.height"
      :venc="thresholdTuningData.venc"
      :initial-threshold="currentOffsetThreshold"
      @close="showThresholdTuning = false"
      @apply="applyOffsetThreshold"
    />
  </div>
</template>

<style scoped>
.vtk-two-view-container {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

/* new */
.vtk-two-view-container > .flex {
  min-height: 0;
}

.viewport-column {
  display: flex;
  flex-direction: column;
  width: 55%;
  height: 100%;
  gap: 10px;
}

.right-column {
  width: 45%;
  height: 100%;
  min-height: 0;
}

.vtk-container {
  position: relative;
  display: flex;
  flex-direction: column;
}

.vtk-view {
  width: 100%;
  border-radius: 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.vtk-container:first-child {
  flex: 1;
}

.vtk-container:last-child {
  flex: 1;
}
.report-section {
  flex: 1 1 0; /* Equal flex with diagram-section */
  min-height: 0; /* Allow flexbox to shrink below content */
  border-radius: 8px;
  padding: 15px;
  overflow-y: auto; /* Scroll if content overflows */
  display: flex;
  flex-direction: column;
}

.diagram-section {
  flex: 1 1 0; /* Equal flex with report-section */
  min-height: 0; /* Allow flexbox to shrink below content */
  border: none;
  padding: 0;
  overflow: hidden;
  background-color: transparent;
}

/* wrappers give each table a bounded height */
.diagram-wrapper {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.flow-tables-container {
  display: flex;
  gap: 20px;
  width: 100%;
}

.tables-section {
  flex: 2;
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.category-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.tool-active {
  background: rgba(var(--v-theme-primary), 0.15);
  border-bottom: 2px solid var(--md-primary);
  color: var(--md-primary);
}

.tool-button {
  position: relative;
}

.tool-button .stacked-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  opacity: 0.7;
}

.tool-button .button-index {
  position: absolute;
  top: 50%;
  left: 1px;
  transform: translateY(-50%);
  font-size: 9px;
  font-weight: bold;
  line-height: 1;
  opacity: 0.8;
}

.flow-controls-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
}

.flow-control-strip {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.flow-control-strip::-webkit-scrollbar {
  display: none;
}

.scroll-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: var(--md-surface-container-high);
  border: 1px solid var(--md-outline-variant);
  color: var(--md-on-surface-variant);
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  z-index: 2;
  font-size: 14px;
  transition: all 0.15s ease;
}

.scroll-arrow:hover {
  background: var(--md-on-surface-variant);
  color: var(--md-surface);
}

.scroll-arrow-left {
  margin-right: 4px;
}

.scroll-arrow-right {
  margin-left: 4px;
}

.overlay-threshold-slider {
  height: 12px;
  gap: 8px;
  margin: -7px 0;
}
.overlay-threshold-slider input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 2px;
  background: var(--md-outline-variant);
  border-radius: 1px;
  outline: none;
  cursor: pointer;
}

.overlay-threshold-slider input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 10px;
  border-radius: 40%;
  background: var(--md-on-surface-variant);
  cursor: pointer;
}
.overlay-threshold-slider input[type='range']::-moz-range-thumb {
  width: 20px;
  height: 10px;
  border-radius: 40%;
  background: var(--md-on-surface-variant);
  cursor: pointer;
  border: none;
}
.phantom-btn-anchor {
  position: relative;
}
.phantom-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  z-index: 20;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4));
}
.phantom-popover::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: var(--md-outline);
}

.phantom-pop-enter-active,
.phantom-pop-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.phantom-pop-enter-from,
.phantom-pop-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
